import React, { useState } from "react";
import { PaziaModal } from "components/molecules";
import { PaziaButton, PaziaLoader, PaziaQuill } from "components/atoms";
import { ModalHeader } from "../../components/templates/Modals/ModalHeader";
import { ModalPadding } from "../../components/templates/Modals/ModalPadding";
import { useCourseGroupAttributesAll } from "api/coursegroup-attributes/useCourseGroupAttributes";
import {
  CourseGroupAttributeApi,
  CourseGroupAttributeType,
  coursegroupAttributesApi,
  BulkAssignAttributeValueModel,
} from "api/coursegroup-attributes/coursegroupAttributesApi";
import { useToaster } from "utils/toaster";

interface AttributeValueState {
  boolValue: boolean;
  textValue: string;
  selectedOptionIds: string[];
}

interface BulkAssignAttributesModalProps {
  isOpen: boolean;
  onCloseRequest: () => void;
  selectedCourseGroupIds: number[];
  onSuccess: () => void;
}

export const BulkAssignAttributesModal = ({
  isOpen,
  onCloseRequest,
  selectedCourseGroupIds,
  onSuccess,
}: BulkAssignAttributesModalProps) => {
  const { isLoading: loadingAttributes, allAttributes } = useCourseGroupAttributesAll();
  const [selectedAttributeIds, setSelectedAttributeIds] = useState<Set<number>>(new Set());
  const [attributeValues, setAttributeValues] = useState<Map<number, AttributeValueState>>(new Map());
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError } = useToaster();

  const toggleAttribute = (attr: CourseGroupAttributeApi) => {
    setSelectedAttributeIds((prev) => {
      const next = new Set(prev);
      if (next.has(attr.id)) {
        next.delete(attr.id);
        setAttributeValues((prevValues) => {
          const nextValues = new Map(prevValues);
          nextValues.delete(attr.id);
          return nextValues;
        });
      } else {
        next.add(attr.id);
        setAttributeValues((prevValues) => {
          const nextValues = new Map(prevValues);
          nextValues.set(attr.id, { boolValue: false, textValue: "", selectedOptionIds: [] });
          return nextValues;
        });
      }
      return next;
    });
  };

  const updateValue = (attrId: number, partial: Partial<AttributeValueState>) => {
    setAttributeValues((prev) => {
      const next = new Map(prev);
      const current = next.get(attrId) ?? { boolValue: false, textValue: "", selectedOptionIds: [] };
      next.set(attrId, { ...current, ...partial });
      return next;
    });
  };

  const buildValues = (): BulkAssignAttributeValueModel[] => {
    const values: BulkAssignAttributeValueModel[] = [];
    for (const attr of allAttributes) {
      const state = attributeValues.get(attr.id);
      if (!state || !selectedAttributeIds.has(attr.id)) continue;

      values.push({
        courseGroupAttributeId: attr.id,
        boolValue: attr.attributeType === CourseGroupAttributeType.Boolean ? state.boolValue : null,
        textValue:
          attr.attributeType === CourseGroupAttributeType.Text ||
          attr.attributeType === CourseGroupAttributeType.Textarea ||
          attr.attributeType === CourseGroupAttributeType.Wysiwyg
            ? state.textValue
            : null,
        selectedOptionIds:
          attr.attributeType === CourseGroupAttributeType.DictionarySingleSelect ||
          attr.attributeType === CourseGroupAttributeType.DictionaryMultiSelect
            ? state.selectedOptionIds.map(Number)
            : [],
      });
    }
    return values;
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      await coursegroupAttributesApi.bulkAssign({
        courseGroupIds: selectedCourseGroupIds,
        courseGroupAttributeIds: Array.from(selectedAttributeIds),
        values: buildValues(),
      });
      showSuccess("Przypisano atrybuty");
      onSuccess();
      handleClose();
    } catch {
      showError("Nie udalo sie przypisac atrybutow");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedAttributeIds(new Set());
    setAttributeValues(new Map());
    onCloseRequest();
  };

  return (
    <PaziaModal id="bulk-assign-attributes-modal" isOpen={isOpen} onCloseRequest={handleClose} size="full">
      <ModalPadding>
        <ModalHeader>Przypisz atrybuty ({selectedCourseGroupIds.length} grup)</ModalHeader>
        {loadingAttributes ? (
          <PaziaLoader position="center" />
        ) : (
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {allAttributes.map((attr) => {
              const checked = selectedAttributeIds.has(attr.id);
              const state = attributeValues.get(attr.id);
              return (
                <div key={attr.id} className="rounded border border-slate-200 p-2 dark:border-slate-700">
                  <label className="flex cursor-pointer items-center gap-2 rounded hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAttribute(attr)}
                      className="form-checkbox"
                    />
                    <span>{attr.name}</span>
                  </label>
                  {checked && state && (
                    <div className="ml-6 mt-2">
                      <AttributeValueEditor
                        attr={attr}
                        state={state}
                        onChange={(partial) => updateValue(attr.id, partial)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
            {allAttributes.length === 0 && <p className="text-sm text-slate-500">Brak dostepnych atrybutow</p>}
          </div>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <PaziaButton type="secondary" onClick={handleClose} disabled={saving}>
            Anuluj
          </PaziaButton>
          <PaziaButton
            type="primary"
            onClick={handleSubmit}
            disabled={selectedAttributeIds.size === 0 || saving}
            processing={saving}
          >
            Przypisz
          </PaziaButton>
        </div>
      </ModalPadding>
    </PaziaModal>
  );
};

interface AttributeValueEditorProps {
  attr: CourseGroupAttributeApi;
  state: AttributeValueState;
  onChange: (partial: Partial<AttributeValueState>) => void;
}

const AttributeValueEditor = ({ attr, state, onChange }: AttributeValueEditorProps) => {
  switch (attr.attributeType) {
    case CourseGroupAttributeType.Boolean:
      return (
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            className="form-checkbox"
            checked={state.boolValue}
            onChange={(e) => onChange({ boolValue: e.target.checked })}
          />
          <span className="text-sm text-slate-600 dark:text-slate-300">Tak</span>
        </label>
      );

    case CourseGroupAttributeType.Text:
      return (
        <input
          type="text"
          className="form-input w-full"
          placeholder="Wartość..."
          value={state.textValue}
          onChange={(e) => onChange({ textValue: e.target.value })}
        />
      );

    case CourseGroupAttributeType.Textarea:
      return (
        <textarea
          className="form-textarea w-full"
          rows={3}
          placeholder="Wartość..."
          value={state.textValue}
          onChange={(e) => onChange({ textValue: e.target.value })}
        />
      );

    case CourseGroupAttributeType.Wysiwyg:
      return <PaziaQuill value={state.textValue} onChange={(html) => onChange({ textValue: html })} />;

    case CourseGroupAttributeType.DictionarySingleSelect:
      return (
        <select
          className="form-select w-full"
          value={state.selectedOptionIds[0] ?? ""}
          onChange={(e) => onChange({ selectedOptionIds: e.target.value ? [e.target.value] : [] })}
        >
          <option value="">-- Wybierz --</option>
          {attr.options.map((opt) => (
            <option key={opt.id} value={opt.id.toString()}>
              {opt.value}
            </option>
          ))}
        </select>
      );

    case CourseGroupAttributeType.DictionaryMultiSelect:
      return (
        <div className="flex flex-wrap gap-2">
          {attr.options.map((opt) => {
            const optValue = opt.id.toString();
            const checked = state.selectedOptionIds.includes(optValue);
            return (
              <label
                key={opt.id}
                className="flex cursor-pointer items-center gap-1 rounded border border-slate-200 px-2 py-1 dark:border-slate-600"
              >
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={checked}
                  onChange={() => {
                    if (checked) {
                      onChange({ selectedOptionIds: state.selectedOptionIds.filter((v) => v !== optValue) });
                    } else {
                      onChange({ selectedOptionIds: [...state.selectedOptionIds, optValue] });
                    }
                  }}
                />
                <span className="text-sm">{opt.value}</span>
              </label>
            );
          })}
        </div>
      );

    default:
      return null;
  }
};
